import {
  Badge,
  Button,
  Dialog,
  Flex,
  Portal,
  Text,
} from "@chakra-ui/react";
import { LightMode } from "../ui/color-mode";
import { AppColors } from "@/styles/colors";
import { useMemo } from "react";
import dayjs from "dayjs";

export interface SuppliesOrderAttempt {
  id: number;
  idPedido: number;
  nroIntento: number;
  motivoNoEntregado?: string;
  fecha: string;
}

type AttemptItem =
  | SuppliesOrderAttempt
  | { id: -1; idPedido: -1; nroIntento: number; motivoNoEntregado?: string; fecha?: string };

interface Props {
  open: boolean;
  setOpen: (flag: boolean) => void;
  pedidoLabel?: string;
  pedidoId?: number | string;
  toneladasOrLitrosLabel: string;
  despachoLabel: string;
  attempts: SuppliesOrderAttempt[];
  showNextAttemptPlaceholder?: boolean;
}

export const SuppliesOrderAttemptsModal = ({
  open,
  setOpen,
  pedidoLabel,
  pedidoId,
  toneladasOrLitrosLabel,
  despachoLabel,
  attempts,
  showNextAttemptPlaceholder = false,
}: Props) => {
  const nextAttemptNumber = useMemo(() => {
    const max = Math.max(0, ...(attempts ?? []).map((a) => Number(a?.nroIntento ?? 0)));
    return max + 1;
  }, [attempts]);

  const attemptItems: AttemptItem[] = useMemo(() => {
    if (!showNextAttemptPlaceholder) return attempts ?? [];
    return [{ id: -1, idPedido: -1, nroIntento: nextAttemptNumber }, ...(attempts ?? [])];
  }, [attempts, nextAttemptNumber, showNextAttemptPlaceholder]);

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content width="90vw" maxWidth="900px" overflow="hidden">
              <Dialog.Header paddingY="16px" paddingLeft="24px">
                <Dialog.Title
                  fontWeight={600}
                  color={AppColors.GRAY_TEXT}
                  fontSize="18px"
                >
                  Intentos
                </Dialog.Title>
              </Dialog.Header>

              <Dialog.Body paddingX="24px" paddingY="16px" width="100%">
                <Flex
                  flexDir="row"
                  maxW="100%"
                  overflowX="auto"
                  gap="12px"
                  paddingBottom="4px"
                >
                  {attemptItems?.map(
                    (attempt, idx) => {
                      const isPlaceholder = attempt?.id === -1;
                      let formattedFecha = "-";
                      if (attempt?.fecha && dayjs(attempt.fecha).isValid()) {
                        formattedFecha = dayjs(attempt.fecha).format("DD/MM/YYYY HH:mm");
                      }

                      const pedidoText =
                        pedidoId !== undefined && pedidoId !== null
                          ? `N° Pedido: ${pedidoId}`
                          : (pedidoLabel ?? "-");

                      return (
                        <Flex
                          key={`${attempt?.id ?? idx}-${attempt?.nroIntento ?? idx}`}
                          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
                          borderRadius="8px"
                          padding="16px"
                          flexDir="column"
                          gap="10px"
                          minW={{ base: "260px", md: "320px" }}
                          w={{ base: "260px", md: "320px" }}
                          maxW={{ base: "260px", md: "320px" }}
                          flex="0 0 auto"
                        >
                          <Flex flexDir="column" gap="6px">
                            <Flex
                              padding="12px"
                              borderRadius="8px"
                              backgroundColor={AppColors.BACKGROUND_SECONDARY}
                            >
                              <Text fontWeight={600} color={AppColors.GRAY_TEXT}>
                                {pedidoText}
                              </Text>
                            </Flex>

                            <Flex padding="12px" borderRadius="8px">
                              <Text fontWeight={600} color={AppColors.GRAY_TEXT}>
                                {toneladasOrLitrosLabel}
                              </Text>
                            </Flex>

                            <Flex
                              padding="12px"
                              borderRadius="8px"
                              backgroundColor={AppColors.BACKGROUND_SECONDARY}
                            >
                              <Text fontWeight={600} color={AppColors.GRAY_TEXT}>
                                Fecha: {formattedFecha}
                              </Text>
                            </Flex>

                            <Flex
                              padding="12px"
                              borderRadius="8px"
                            >
                              <Text fontWeight={600} color={AppColors.GRAY_TEXT}>
                                {despachoLabel}
                              </Text>
                            </Flex>
                          </Flex>
                          
                          <Flex height="1px" backgroundColor={AppColors.WHITE_BORDER_1} marginY="4px"/>
                          <Flex alignItems="center" gap="8px">
                            <Badge bg={AppColors.YELLOW_SECONDARY}>
                              <Text
                                paddingY="2px"
                                paddingX="8px"
                                fontSize="12px"
                                color={AppColors.WHITE_SECONDARY}
                              >
                                Intento {attempt?.nroIntento}
                              </Text>
                            </Badge>
                            {!isPlaceholder && (
                              <Badge bg={AppColors.RED_ICON}>
                                <Text
                                  paddingY="2px"
                                  paddingX="8px"
                                  fontSize="12px"
                                  color={AppColors.WHITE_SECONDARY}
                                >
                                  No entregado
                                </Text>
                              </Badge>
                            )}
                          </Flex>

                          <Flex
                            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
                            borderRadius="8px"
                            padding="12px"
                            backgroundColor={AppColors.BACKGROUND_SECONDARY}
                            minH="84px"
                          >
                            <Text
                              fontSize="12px"
                              color={
                                isPlaceholder
                                  ? AppColors.GRAY_TEXT_PLACEHOLDER
                                  : AppColors.GRAY_TEXT
                              }
                            >
                              {isPlaceholder
                                ? "Si el intento de entrega resulta no entregado, mostrará aquí su motivo"
                                : attempt?.motivoNoEntregado || "-"}
                            </Text>
                          </Flex>
                        </Flex>
                      );
                    },
                  )}
                </Flex>
              </Dialog.Body>

              <Dialog.Footer paddingY="16px" paddingX="24px">
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" paddingX="16px" fontWeight={600}>
                    Cerrar
                  </Button>
                </Dialog.ActionTrigger>
              </Dialog.Footer>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
